import React from 'react'
import styled from '@emotion/styled'
import { SvgLazy } from '@mobi/component-library/Assets/Svg/SvgLazy'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { dayjs } from '@mobi/utils/date'
import { pluralize } from '@mobi/utils/string/pluralize'
import { FlameStreakBadge } from '@core/Areas/BetLoop/Components/Common/FlameStreak'
import { ProfileAvatar } from '@core/Areas/BetLoop/Components/Common/ProfileAvatar'
import type { BetLoopSharedBet, ProfileType } from '@core/Areas/BetLoop/types'

const enum LocalConstants {
  HeaderProfileButtonClass = 'js-header-profile_button',
  HeaderSponsoredTipsterClass = 'js-tipster-header_label',
  HeaderWinStreakIconClass = 'js-header-win-streak_label',
  HeaderSharedAgoTextClass = 'js-header-shared-ago_label',
  HeaderIndustryTipsterLogoClass = 'js-header-industry-logo_label',
}

export const Header: React.FC<{
  sharedBet: BetLoopSharedBet
  isOnProfilePage?: boolean
  avatarProfileRef?: React.RefObject<HTMLDivElement | null>
  handleProfileClick?(): void
  rightAlignedButton?: React.ReactNode
  isForExternalShare?: boolean
}> = ({
  sharedBet,
  handleProfileClick,
  isOnProfilePage,
  avatarProfileRef,
  rightAlignedButton,
  isForExternalShare,
}) => {
  const currentWinStreak = sharedBet.ownerCurrentWinStreak
  const shouldDisplayWinStreak = !sharedBet.isResulted && currentWinStreak >= 3

  const followerCount = sharedBet.ownerAccountFollowerCount

  const sharedAgoTextDisplay = React.useMemo(
    () => getShareTimeText(sharedBet.createdOnUtc),
    [sharedBet.createdOnUtc]
  )

  return (
    <HeaderStyled
      className={
        sharedBet.ownerAccountType === 'Profile' ? '' : LocalConstants.HeaderSponsoredTipsterClass
      }
      style={{ background: getHeaderBackgroundColor(sharedBet.ownerAccountType) }}
    >
      <button
        type='button'
        onClick={handleProfileClick}
        className={!isOnProfilePage ? LocalConstants.HeaderProfileButtonClass : ''}
      >
        <div ref={avatarProfileRef}>
          <ProfileAvatar
            avatar={sharedBet.ownerAccountAvatar}
            color={sharedBet.ownerAccountColor}
          />
        </div>

        <div>
          <span>{sharedBet.ownerAccountDisplayName}</span>
          <div>
            <span>{`${followerCount} ${pluralize(followerCount, 'Follower')}`}</span>
            {!isForExternalShare && (
              <>
                <span> · </span>
                <span className={LocalConstants.HeaderSharedAgoTextClass}> Shared </span>
                <span>{sharedAgoTextDisplay}</span>
              </>
            )}
          </div>
        </div>
      </button>

      <div>
        {shouldDisplayWinStreak && (
          <span className={LocalConstants.HeaderWinStreakIconClass} aria-label='Current win streak'>
            <FlameStreakBadge value={currentWinStreak} />
          </span>
        )}

        {sharedBet.ownerAccountType !== 'Profile' && (
          <div className={LocalConstants.HeaderIndustryTipsterLogoClass}>
            {sharedBet.ownerAccountType === 'ExpertTipster' && (
              <Icon type='tabtouchLogoCompact' size='3.2rem' color={colors.lavender[750]} />
            )}
            {sharedBet.ownerAccountType === 'GiddyUp' && (
              <SvgLazy name='GiddyUpLogoCompact' color='#0051A0' width='3.5em' />
            )}
          </div>
        )}
        {rightAlignedButton}
      </div>
    </HeaderStyled>
  )
}

// =============
// Local Helpers
// =============

function getHeaderBackgroundColor(type?: ProfileType) {
  switch (type) {
    case 'ExpertTipster':
      return 'linear-gradient(90deg, #E9CDFF 0%, #fff 100%)'
    case 'GiddyUp':
      return 'linear-gradient(90deg, #BDDEFF 0%, #FFF 100%)'
    default:
      return colors.white
  }
}

const getShareTimeText = (createdOnUtc: string) => {
  const now = dayjs()
  const shareTime = dayjs(createdOnUtc)

  const min = now.diff(shareTime, 'm')
  const hrs = now.diff(shareTime, 'h')
  const days = now.diff(shareTime, 'd')
  const weeks = now.diff(shareTime, 'w')
  const months = now.diff(shareTime, 'M')
  const years = now.diff(shareTime, 'y')

  if (min < 5) return 'just now'
  if (min < 60) return `${min}m ago`
  if (hrs < 24) return `${hrs}h ago`
  if (days < 7) return `${days}d ago`
  if (days < 28) return `${weeks}w ago`
  if (days < 31) return '1mo ago'
  if (days < 365) return `${months}mo ago`
  return `${years}y ago`
}

// ======
// Styles
// ======

const HeaderStyled = styled.header({
  display: 'flex',
  justifyContent: 'space-between',
  borderRadius: `${radius.lgx2} ${radius.lgx2} 0 0`,
  padding: `${spacing.sm} ${spacing.sm} 0 ${spacing.sm}`,

  [`&.${LocalConstants.HeaderSponsoredTipsterClass}`]: {
    padding: spacing.sm,
  },

  '> button:first-of-type': {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.smx1,
    textAlign: 'left',
    background: 0,
    border: 0,
    padding: 0,
    opacity: 1,
    transition: 'opacity 0.25s ease',
    WebkitTapHighlightColor: 'transparent',
    touchAction: 'manipulation',
    minWidth: 0,

    [`&.${LocalConstants.HeaderProfileButtonClass}`]: {
      '&:active, &:hover': { opacity: 0.5 },
      cursor: 'pointer',
    },

    '> div:last-of-type': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      fontFamily: font.family.primary,
      flex: '1 1 auto',
      minWidth: 0,

      // display name
      '> span:first-of-type': {
        fontSize: font.size.lg.fontSize,
        fontWeight: font.weight.medium,
        lineHeight: font.size.lg.lineHeight,
        color: colors.black,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
        display: 'block',
      },

      '> div': {
        fontSize: font.size.md.fontSize,
        fontWeight: font.weight.regular,
        lineHeight: font.size.md.lineHeight,
        color: colors.neutral[800],

        [`> .${LocalConstants.HeaderSharedAgoTextClass}`]: {
          display: 'none',
          '@media (min-width: 418px)': { display: 'inline' },
        },
      },
    },
  },

  '> div:first-of-type': {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.smx2,
    paddingLeft: spacing.smx2,

    [`> .${LocalConstants.HeaderWinStreakIconClass}`]: {
      display: 'none',

      '@media (min-width: 338px)': {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },

    [`> .${LocalConstants.HeaderIndustryTipsterLogoClass}`]: {
      alignSelf: 'center',
      display: 'none',
      width: '3.6rem',

      '@media (min-width: 374px)': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
  },
})
